import React, { useState } from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import { useConsultation } from "../../ConsultationContext";
import BioAgeClockCard from "../shared/BioAgeClockCard";
import ConsultationVideoOverlay from "../shared/ConsultationVideoOverlay";

// Outro video shown full-viewport when the affiliate clicks "Complete" on
// the last sub-step of Section 3. Self-hosted MP4 (Azure blob); will become
// per-affiliate config later alongside the intro URL.
const OUTRO_VIDEO_URL =
  "https://mypnoe.blob.core.windows.net/consultation/Outro_Video_Digital_Consult_S1.mp4";

/**
 * Bottom-bar stepper for the consultation experience.
 *
 * Visual reference: Figma node 14483:56019 (variant set).
 *
 * Behaviour per Figma:
 *   - Center label: "Step N · <SectionLabel>" + N sub-step dots
 *     (one dot per sub-step in the CURRENT section, not per section).
 *   - Left side switches by position:
 *       a) Sub-step 0 of section 1 (intro)            → arrows-only
 *       b) Sub-step 0 of section 2/3 (section entry)  → "Bio age" / "Deep dive"
 *          jump-back pill button + mini bio-age summary
 *       c) Mid sub-steps                              → ← arrow + mini bio-age
 *   - Right side:
 *       a) Mid sub-steps  → → arrow
 *       b) Last sub-step of section 1/2 → CTA "Deep dive" / "Protocol"
 *       c) Last sub-step of section 3   → CTA "Complete" (disabled)
 */
const ConsultationStepper: React.FC = () => {
  const theme: Theme = useTheme();
  const { stepper, data, aiActive } = useConsultation();
  const { current, isFirst, isLast, goNext, goPrev, goToSection, goToSub } =
    stepper;

  // In AI-guided mode the agent drives navigation, so the prev/next controls
  // are hidden from the user — but kept mounted (`display: none`, not
  // unmounted) so the ElevenLabs `clickDom` tool can still find and `.click()`
  // the `consultation-prev` / `consultation-next` targets. `.click()` fires on
  // a `display:none` element, so hiding this way doesn't break the agent.
  const hideNav = aiActive ? { display: "none" } : null;

  const sectionId = current.section.id;
  const subs = current.section.subs;
  const subIndex = current.subIndex;

  // Outro overlay state — set true when the affiliate clicks "Complete" on
  // the last sub-step of Section 3; the full-viewport `ConsultationVideoOverlay`
  // takes over until the video ends or the user clicks Skip.
  const [outroPlaying, setOutroPlaying] = useState(false);
  const isFirstOfSection = subIndex === 0;
  const isLastOfSection = subIndex === subs.length - 1;

  // Bio-age summary appears in stepper from Section 2 onwards.
  const showBioAgeSummary = sectionId !== "bio-age";
  // Section 3 also shows the target age (e.g. "33 → 30 Bio age").
  // Deferred for v1 — `bioAgeProjection` is not shipped by the current
  // `/api/v2/Precision/Report/GetConsultation` endpoint (discrepancy #2 in
  // claude_docs/CONSULTATION_BACKEND_BRIEF.md). Force the target chip off
  // until the backend provides `{ targetBioAge, ... }`.
  const showTarget = false; // sectionId === "programming";
  // `prep` may still be null on initial render (consultation-prep fetch in
  // flight) — fall back to current bio age so the stepper renders something
  // sane until the value lands.
  const targetBioAge = data.bioAge.biological;
  // Original (re-enable with bioAgeProjection in v2):
  // const targetBioAge =
  //   data.prep?.bioAgeProjection.targetBioAge ?? data.bioAge.biological;

  // Section transitions:
  //   - first sub-step of Section 2 → "Bio age" jump-back pill on left
  //   - first sub-step of Section 3 → "Deep dive" jump-back pill on left
  //   - last sub-step of Section 1  → "Deep dive" CTA on right
  //   - last sub-step of Section 2  → "Protocol" CTA on right
  //   - last sub-step of Section 3  → no CTA (nothing to advance to)
  // The jump-back pills navigate via `goPrev` (not `goToSection`) so the user
  // lands on the LAST sub-step of the previous section (where they came
  // from), not the first.
  const leftJumpBack =
    isFirstOfSection && sectionId === "systems"
      ? { label: "PNOĒ age" }
      : isFirstOfSection && sectionId === "programming"
      ? { label: "Deep dive" }
      : null;

  // Right-side CTA — section-transition button on the last sub-step of
  // Section 1 / 2, and the "Complete" button on the last sub-step of
  // Section 3 (which triggers the outro video instead of navigating).
  type CtaTransition = { label: string; target: "systems" | "programming" };
  type CtaComplete = { label: string; target: "complete" };
  const rightCta: CtaTransition | CtaComplete | null =
    isLastOfSection && sectionId === "bio-age"
      ? { label: "Deep dive", target: "systems" }
      : isLastOfSection && sectionId === "systems"
      ? { label: "Protocol", target: "programming" }
      : isLastOfSection && sectionId === "programming"
      ? { label: "Complete", target: "complete" }
      : null;

  const handleCta = () => {
    if (!rightCta) return;
    if (rightCta.target === "complete") {
      setOutroPlaying(true);
      return;
    }
    goToSection(rightCta.target);
  };

  return (
    <>
      {outroPlaying && (
        <ConsultationVideoOverlay
          videoUrl={OUTRO_VIDEO_URL}
          showSkip={false}
          autoPlay
          onComplete={() => {
            // Deliberately a no-op — when the outro finishes we leave the
            // full-viewport video end-screen mounted instead of unmounting
            // the overlay or trying to close the tab. Letting the user sit
            // on the natural end-of-video screen is the desired UX.
          }}
        />
      )}
      <Box component="footer" sx={styles.footer(theme)}>
        <Box sx={styles.left}>
          {leftJumpBack ? (
            <Box
              component="button"
              type="button"
              onClick={goPrev}
              data-agent-target="consultation-prev"
              sx={[styles.jumpBackPill(theme), hideNav]}
            >
              {leftJumpBack.label}
            </Box>
          ) : !isFirst ? (
            <Box
              component="button"
              type="button"
              onClick={goPrev}
              aria-label="Previous step"
              data-agent-target="consultation-prev"
              sx={[styles.arrowBtn(theme, false), hideNav]}
            >
              ‹
            </Box>
          ) : null}

          {showBioAgeSummary && (
            <Box sx={styles.bioAgeChip}>
              <BioAgeClockCard size={32} compact showSubtitle={false} />
              {showTarget && (
                <>
                  <Box component="span" sx={styles.arrowGlyph(theme)}>
                    →
                  </Box>
                  <Box component="span" sx={styles.targetValue(theme)}>
                    {targetBioAge}
                  </Box>
                </>
              )}
              <Box component="span" sx={styles.bioAgeLabel(theme)}>
                PNOĒ age
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={styles.center}>
          <Box sx={styles.stepLabel(theme)}>
            <Box component="span">Step {current.sectionIndex + 1}</Box>
            <Box component="span" sx={styles.dotSeparator(theme)} />
            <Box component="span">{current.section.label}</Box>
          </Box>
          <Box role="tablist" aria-label="Section sub-steps" sx={styles.dots}>
            {subs.map((s, i) => {
              const completed = i < subIndex;
              const active = i === subIndex;
              return (
                <Box
                  key={s.id}
                  component="button"
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-label={s.label}
                  onClick={() => goToSub(s.id)}
                  sx={styles.dot(theme, active, completed)}
                />
              );
            })}
          </Box>
        </Box>

        <Box sx={styles.right}>
          {rightCta ? (
            <PrimaryButton
              label={rightCta.label}
              onClick={handleCta}
              data-agent-target="consultation-next"
              sx={{ ...styles.cta, ...(aiActive ? { display: "none" } : {}) }}
            />
          ) : !isLast ? (
            <Box
              component="button"
              type="button"
              onClick={goNext}
              aria-label="Next step"
              data-agent-target="consultation-next"
              sx={[styles.arrowBtn(theme, false), hideNav]}
            >
              ›
            </Box>
          ) : null}
        </Box>
      </Box>
    </>
  );
};

export default ConsultationStepper;

const styles = {
  footer: (theme: Theme) => ({
    position: "sticky" as const,
    bottom: 0,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    padding: "8px 16px",
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    gap: "16px",
    boxShadow:
      "0px 2px 8px rgba(0, 0, 0, 0.04), 0px 8px 20px rgba(0, 0, 0, 0.06)",
    zIndex: 10,
  }),
  left: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  center: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "8px",
  },
  right: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "12px",
  },
  arrowBtn: (theme: Theme, disabled: boolean) => ({
    width: "32px",
    height: "32px",
    borderRadius: "16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.4 : 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    padding: 0,
    fontFamily: "var(--font-Medium)",
  }),
  jumpBackPill: (theme: Theme) => ({
    border: `1px solid ${theme.colors_ds.PRIMARY_TEXT_MAIN}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    borderRadius: "6px",
    padding: "8px 12px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    cursor: "pointer",
    height: "32px",
    display: "flex",
    alignItems: "center",
  }),
  bioAgeChip: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  arrowGlyph: (theme: Theme) => ({
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    margin: "0 2px",
  }),
  targetValue: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.SUCCESS_ICON_MAIN,
  }),
  bioAgeLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    marginLeft: "4px",
  }),
  cta: {
    height: "32px",
    padding: "8px 12px",
    fontSize: "12px",
    "& > div": { flex: "0 0 auto" },
  },
  stepLabel: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    lineHeight: 1.6,
  }),
  dotSeparator: (theme: Theme) => ({
    width: "4px",
    height: "4px",
    borderRadius: "50%",
    backgroundColor: theme.colorTokens.N[60],
  }),
  dots: {
    display: "flex",
    gap: "4px",
  },
  // All dots are 8px circles. Visited (current + previously-visited) use
  // primary blue; upcoming use neutral gray. No size change between states.
  dot: (theme: Theme, active: boolean, completed: boolean) => ({
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor:
      active || completed
        ? theme.colors_ds.PRIMARY_TEXT_MAIN
        : theme.colorTokens.N[40],
    cursor: "pointer",
    border: "none",
    padding: 0,
    transition: "background-color 120ms ease",
  }),
};
